import { message } from '@nackle/paper';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';

/**
 * Hook for loading initial data in an async function.
 *
 * Automatically handles exceptions thrown by logging the error and showing a message.
 *
 * @param initFunction
 */
export function useInit(initFunction: () => Promise<unknown>): boolean {
    const [isLoading, setLoading] = useState(false);
    useEffect(
        () => {
            initFunction()
                .catch((e) => {
                    console.error(e);
                    message.error({
                        content: 'An error occurred while loading',
                    });
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        // We explicitly don't want to depend on initFunction because it's guaranteed to change next update
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    return isLoading;
}

/**
 * Analogue of bindActionCreators() for hooks
 *
 * React-Redux apparently had a function called "useActions()" at one point and they removed it, but IMO there's still
 * some value (especially when using Thunks) in binding action creators so they can be invoked like normal functions.
 *
 * @param Either a traditional action creator or a "Thunk" action creator
 */
export const useActionCreator = (
    // `actionCreator()` here returns `any` because it's either `Action` or `ThunkAction`, and it's easier to express
    // the overloads with a variable cast than to do it here.
    <P extends any[]>(actionCreator: (...args: P) => any) => {
        const dispatch = useDispatch();

        return useCallback(
            (...args: P) => dispatch(actionCreator(...args)),
            [actionCreator, dispatch]
        );
    }
) as {
    // There are separate overloads for traditional action creators versus Thunk action creators because Redux Thunk
    // behaves differently for traditional actions versus Thunk actions

    <P extends any[], A extends Action>(actionCreator: (...args: P) => A): (...args: P) => A;
    <P extends any[], R, S, E, A extends Action>(actionCreator: (...args: P) => ThunkAction<R, S, E, A>): (...args: P) => R;
};

/**
 * Wrapper for React-Redux's useSelector() that makes it easy to "parameterize" selectors.
 *
 * One of my biggest gripes with `mapStateToProps()` is that components have no way to tell their connect()
 * wrapper what data they want to receive from the state. You can either:
 *
 * 1) Write the ID of what you want in the Redux store -- Because the ID is stored in global state, it only works when
 *    EXACTLY one instance of the component is loaded at the same time.
 *
 *      function fooMapStateToProps(state) {
 *          const { barId } = state.fooEditor;
 *          return {
 *              ...state.fooEditor,
 *              barDetails: state.barDetails[barId]
 *          }
 *      }
 *      class FooEditor {
 *          render() {
 *              <Form>
 *                  <BarDropdown bar={this.props.id} options={this.props.bars} />
 *                  <BarDetails details={this.props.barDetails} />
 *              </Form>
 *          }
 *      }
 *
 * 2) Insert a second Redux connect() in the middle of your component hierarchy, wrapping a child component.
 *    This doubles the number of files in your project and the number of places you have to check whether
 *    mapStateToProps() matches the component's signature.
 *
 *      function fooMapStateToProps(state) {
 *          return {
 *              ...state.fooEditor,
 *          }
 *      }
 *      class FooEditor {
 *          render() {
 *              <Form>
 *                  <BarDropdown bar={this.props.id} options={this.props.bars} />
 *                  <ConnectedBarDetails barId={this.props.barId} />
 *              </Form>
 *          }
 *      }
 *      function barMapStateToProps(state, props) {
 *          return {
 *              barDetails: state.barDetails[props.barId];
 *          };
 *      }
 *      function BarDetails() {...}
 *
 * So to me, useSelector() is an underrated godsend. You can tell React-Redux exactly what you want it to pass your
 * component from INSIDE that component!
 *
 *      function FooEditor() {
 *          const barDetails = useSelector(getBarDetails);
 *      }
 *
 * It's limited, however -- useSelector() only calls your function with one argument - the state. But if the selector
 * is actually a function that *returns* a selector, we can use the first invocation's arguments to customize the
 * selector: (This is called "currying" - https://wikipedia.org/wiki/Currying)
 *
 *      const getBarDetails = (barId: string) => (state) => state.barDetails[barId];
 *
 *      function FooEditor({ barId }) {
 *          const barDetails = useSelector(getBarDetails(barId);
 *      }
 *
 * The selector factory returns a new function reference every time it's called. According to the Rules of Hooks,
 * it must be memoized: (https://reactjs.org/docs/hooks-rules.html)
 *
 *      function FooEditor({ barId }) {
 *          const barDetails = useSelector(useMemo(() => getBarDetails(barId), [barId]));
 *      }
 *
 * This function is just a wrapper to make the syntax more concise:
 *
 *      function FooEditor({ barId }) {
 *          const barDetails = useSelectorFactory(getBarDetails)(barId);
 *      }
 *
 * The reason for the weird calling convention is to fit into both 1) React's expectations that all hook functions
 * have names starting with "use-", and 2) this is the only way I could get Typescript to validate that the parameters
 * match the function signature. If I did:
 *
 *          const barDetails = useSelectorFactory(getBarDetails, [barId]);
 *
 * then Typescript doesn't seem to validate the argument array.
 *
 * @param selectorFactory - Function that returns a selector. The result of invoking the selector does not need to be memoized.
 */
export function useSelectorFactory<P extends any[], S, R>(selectorFactory: (...args: P) => (state: S) => R) {
    // The arguments are supplied in a second function invocation so that Typescript (and my IDE) can verify that
    // arguments match the selector's parameters.
    return function useSelectorFactory(...args: P) {

        // Memoize the parameterized selector
        // The memoizer needs to be able to see both the selector function and its arguments individually,
        // so we have to flatten the args array, but the linter can't comprehend that so we have to suppress it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        const memoizedSelector = useMemo(() => selectorFactory(...args), [selectorFactory, ...args]);

        return useSelector(memoizedSelector);
    };
}
